import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { billsService } from '@/services/billsService'
import { BillListItem, BillVendor } from '@/api/types'

export const BillsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [bills, setBills] = useState<BillListItem[]>([])
  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)
  const [billDateFrom, setBillDateFrom] = useState<string>('')
  const [billDateTo, setBillDateTo] = useState<string>('')

  const branchId = user?.selectedBranch?.branchId

  // Load vendors for filter
  useEffect(() => {
    if (!branchId) return
    const loadVendors = async () => {
      try {
        const vendorList = await billsService.getAllVendors(branchId)
        setVendors(vendorList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadVendors()
  }, [branchId])

  // Load bills
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadBills = async () => {
      try {
        setLoading(true)
        const response = await billsService.getBills(branchId, pageNumber, pageSize)
        const list = response.data || []
        setBills(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('bills.failedToLoadBills'))
      } finally {
        setLoading(false)
      }
    }

    loadBills()
  }, [branchId, pageNumber, pageSize])

  const filteredBills = useMemo(() => {
    return bills.filter((bill) => {
      if (selectedVendorId && bill.vendorId !== selectedVendorId) {
        return false
      }

      if (billDateFrom && bill.billDate && bill.billDate < billDateFrom) {
        return false
      }

      if (billDateTo && bill.billDate && bill.billDate > billDateTo) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      const vendorName = (bill as any).vendorNameEn || (bill as any).vendorNameAr || bill.vendorName || ''
      return (
        (bill.billNo || bill.billNumber || '').toLowerCase().includes(query) ||
        vendorName.toLowerCase().includes(query)
      )
    })
  }, [bills, selectedVendorId, searchQuery, billDateFrom, billDateTo])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleAddBill = () => {
    navigate('/purchases/bills/new')
  }

  const handleEditBill = (bill: BillListItem) => {
    const billId = bill.billId
    if (!billId) {
      toast.error('Invalid bill ID')
      return
    }
    navigate(`/purchases/bills/${billId}/edit`, { state: { bill } })
  }

  const handlePrint = () => {
    toast.success('Print functionality coming soon')
  }

  const handleCopy = () => {
    toast.success('Copy functionality coming soon')
  }

  const handleCsv = () => {
    toast.success('CSV export coming soon')
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleVendorFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedVendorId(undefined)
    } else {
      setSelectedVendorId(Number(value))
    }
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('bills.title')}
          description={t('bills.manageBills')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('bills.branchNotSelected')}
              description={t('bills.selectBranchToViewBills')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('bills.title')}
        description={t('bills.manageBills')}
        action={{
          label: t('bills.addNew'),
          onClick: handleAddBill,
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="p-6">
          {/* Filters */}
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('bills.billDateFrom')}</label>
                <Input
                  type="date"
                  value={billDateFrom}
                  onChange={(e) => setBillDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('bills.billDateTo')}</label>
                <Input
                  type="date"
                  value={billDateTo}
                  onChange={(e) => setBillDateTo(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('bills.vendor')}</label>
                <Select value={selectedVendorId ? String(selectedVendorId) : 'all'} onValueChange={handleVendorFilterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('bills.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bills.all')}</SelectItem>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.vendorId || vendor.id} value={String(vendor.vendorId || vendor.id)}>
                        {vendor.vendorName || vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId || vendor.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search and Actions */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder={t('bills.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  <Copy className="w-4 h-4 mr-2" />
                  {t('bills.copy')}
                </Button>
                <Button variant="outline" size="sm" onClick={handleCsv}>
                  <FileDown className="w-4 h-4 mr-2" />
                  {t('bills.csv')}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2" />
                  {t('bills.print')}
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t('bills.loadingBills')}</p>
            </div>
          ) : filteredBills.length === 0 ? (
            <EmptyState
              title={t('bills.noBillsFound')}
              description={t('bills.getStarted')}
              action={{
                label: t('bills.addNewBillAction'),
                onClick: handleAddBill,
              }}
            />
          ) : (
            <>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('bills.billNo')}</TableHead>
                      <TableHead>{t('bills.vendor')}</TableHead>
                      <TableHead>{t('bills.date')}</TableHead>
                      <TableHead>{t('bills.dueDate')}</TableHead>
                      <TableHead>{t('bills.amount')}</TableHead>
                      <TableHead>{t('bills.attachment')}</TableHead>
                      <TableHead>{t('bills.print')}</TableHead>
                      <TableHead>{t('bills.edit')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBills.map((bill) => (
                      <TableRow key={bill.billId}>
                        <TableCell>{bill.billNo || bill.billNumber || 'N/A'}</TableCell>
                        <TableCell>{(bill as any).vendorNameEn || (bill as any).vendorNameAr || bill.vendorName || 'N/A'}</TableCell>
                        <TableCell>{formatDate(bill.billDate)}</TableCell>
                        <TableCell>{formatDate(bill.dueDate)}</TableCell>
                        <TableCell>{formatAmount(bill.amount || bill.total)}</TableCell>
                        <TableCell>
                          {bill.attachment ? (
                            <Button variant="ghost" size="sm">
                              {t('common.view')}
                            </Button>
                          ) : (
                            t('common.na', { defaultValue: 'N/A' })
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={handlePrint}>
                            <Printer className="w-4 h-4" />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleEditBill(bill)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  {t('common.showing')} {filteredBills.length > 0 ? 1 : 0} {t('common.to')} {filteredBills.length} {t('common.of')} {totalCount} {t('common.entries')}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    {t('common.page')} {pageNumber} {t('common.of')} {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(totalPages, prev + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

